import storageService from 'src/services/storage-service';
import { UserGroup, UserPermissions } from 'src/shared/types';
import { jwtDecode } from 'jwt-decode';
import config from 'src/config';
import authStorageService from 'src/shared/hooks/services/auth-storage-service';
import { WELCOME_DIALOG_DISMISSED_STORAGE_KEY } from 'src/shared/constants/storageKeys';
import { FailureVerdict, retryWithBackoff } from 'src/shared/helpers/retryWithBackoff';
import apiService, { HTTPMethod } from './api-service';

type TokenExchangeResponse = {
    accessToken: string;
    accessTokenExpiresAt: string;
    refreshToken: string;
    refreshTokenExpiresAt: string;
};

type CustomJwtPayload = {
    token: string;
    isInternalUser: boolean;
    tncConsent: boolean;
    cognitoId: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    groups: UserGroup[];
    userEnabled: boolean;
};

/**
 * A 401 or 403 from `/renew` is the server's verdict on this session — the
 * refresh token is expired, revoked, or the account is disabled. Trying again
 * cannot change the answer, so these sign the user out at once. Everything
 * else — a dropped connection, a 502 mid-deploy, a timeout — says nothing
 * about whether the session is still good, and is worth another attempt.
 */
const isSessionRejection = (error: unknown): boolean => {
    const message = (error as Error)?.message ?? '';
    return [401, 403].some(status => message.includes(`status: ${status}`));
};

type MyPermissionsResponse = {
    customerLevelPermissions: UserPermissions['customerLevelPermissions'];
    projectLevelPermissions: UserPermissions['projectLevelPermissions'];
};

export class AuthService {
    private refreshInFlight: Promise<void> | null = null;
    private refreshSessionTimeout: ReturnType<typeof setTimeout> | null = null;

    /** True for the whole renewal, retries included. `useAuthService` keeps
     *  the user on the page while this holds, so it must not go false between
     *  attempts — that would bounce them to the login screen mid-recovery. */
    public get isRefreshing(): boolean {
        return this.refreshInFlight !== null;
    }

    constructor() {
        const accessToken = authStorageService.getAccessToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();

        if (accessToken && accessTokenExpiry && new Date(accessTokenExpiry) > new Date()) {
            this.scheduleAutoRefresh();

            // Hydrate permissions cache on startup if missing
            if (!authStorageService.getPermissions()) {
                this.fetchAndCachePermissions().catch(() => {});
            }
            return;
        }

        const refreshToken = authStorageService.getRefreshToken();
        const refreshTokenExpiry = authStorageService.getRefreshTokenExpiry();

        if (refreshToken && refreshTokenExpiry && new Date(refreshTokenExpiry) > new Date()) {
            this.refreshSession();
        }
    }

    /**
     * Renew the session, retrying a renewal the server has not actually
     * refused. Every engine request funnels through `getAccessToken`, so a
     * burst of them discovers a stale token together; they queue behind one
     * renewal rather than starting a stampede.
     */
    public async refreshSession(): Promise<void> {
        this.refreshInFlight ??= this.renewUntilItSticksOrIsRefused().finally(() => {
            this.refreshInFlight = null;
        });
        return this.refreshInFlight;
    }

    private async renewUntilItSticksOrIsRefused(): Promise<void> {
        const refreshToken = authStorageService.getRefreshToken();
        const refreshTokenExpiry = authStorageService.getRefreshTokenExpiry();

        if (!refreshToken || !refreshTokenExpiry) {
            console.info('Refresh token missing, skipping signout');
            authStorageService.clearAll();
            return;
        }
        if (new Date(refreshTokenExpiry) < new Date()) {
            console.info('Refresh token expired, clearing auth');
            authStorageService.clearAll();
            return;
        }

        try {
            const newAuthData = await retryWithBackoff(() => this.renewOnce(), {
                onFailure: (error, attempt) => {
                    if (isSessionRejection(error)) {
                        return FailureVerdict.Stop;
                    }
                    console.info(`Token refresh attempt ${attempt} failed, retrying:`, (error as Error).message);
                    return FailureVerdict.Retry;
                },
            });
            await this.storeAuthData(newAuthData);
        } catch (error) {
            const reason = (error as Error).message;
            const refused = isSessionRejection(error);
            console.info(
                refused ? 'Session refused on renewal - user will be signed out:' : 'Token refresh kept failing - user will be signed out:',
                reason
            );
            authStorageService.clearAll();
        }
    }

    private renewOnce(): Promise<TokenExchangeResponse> {
        return apiService.fetch(`${config.apiBaseUrl}/renew`, {
            method: HTTPMethod.POST,
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                token: authStorageService.getRefreshToken(),
            }),
            noCache: true,
        });
    }

    /**
     * Signout user
     */
    public async signout(callback?: () => void) {
        const accessToken = authStorageService.getAccessToken();
        // Preserve UI preference keys that should persist across logout
        storageService.clearAllExcept([WELCOME_DIALOG_DISMISSED_STORAGE_KEY]);
        try {
            if (accessToken) {
                await apiService.fetch(`${config.apiBaseUrl}/logout`, {
                    method: HTTPMethod.POST,
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken as string,
                    },
                    ignoreErrors: true,
                });
            }
        } finally {
            callback?.();
            if (window.location.pathname !== '/') {
                const { cognitoLoginUrl, cognitoClientId } = config;
                const locationOrigin = window.location.origin;
                const cognitoRedirectUrl = `${locationOrigin}/oauth?provider=aws`;
                const staticParams = `client_id=${cognitoClientId}&response_type=code&scope=aws.cognito.signin.user.admin`;
                const cognitoLogoutUrl = `${cognitoLoginUrl}logout?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
                window.location.replace(cognitoLogoutUrl);
            }
        }
    }

    private scheduleAutoRefresh() {
        const accessToken = authStorageService.getAccessToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();

        if (!accessToken || !accessTokenExpiry) {
            return;
        }

        if (this.refreshSessionTimeout) {
            clearTimeout(this.refreshSessionTimeout);
        }

        const expiresAt = new Date(accessTokenExpiry).getTime();
        let autoRefreshDelayInMilliseconds = expiresAt - Date.now() - 5 * 60 * 1000;

        if (autoRefreshDelayInMilliseconds < 0) {
            autoRefreshDelayInMilliseconds = 0;
        }

        this.refreshSessionTimeout = setTimeout(() => {
            this.refreshSession();
        }, autoRefreshDelayInMilliseconds);
    }

    /**
     * Store token data in local storage and refresh permissions.
     * @returns Cached permissions after refresh, or null if refresh failed or there was no access token.
     */
    private async storeAuthData(authData: TokenExchangeResponse): Promise<UserPermissions | null> {
        if (!authData.accessToken) {
            return null;
        }
        authStorageService.setAccessToken(authData.accessToken);
        authStorageService.setRefreshToken(authData.refreshToken);
        authStorageService.setAccessTokenExpiry(authData.accessTokenExpiresAt);
        authStorageService.setRefreshTokenExpiry(authData.refreshTokenExpiresAt);

        this.scheduleAutoRefresh();

        window.dispatchEvent(
            new CustomEvent('auth_data_refreshed', {
                detail: {
                    accessToken: authData.accessToken,
                    accessTokenExpiry: authData.accessTokenExpiresAt,
                },
                bubbles: true,
            })
        );

        // Refresh permissions immediately after token renewal
        return await this.fetchAndCachePermissions().catch(err => {
            console.info('Permissions refresh failed silently:', (err as Error).message);
            return null;
        });
    }

    /**
     * Fetch user permissions from the dedicated /my_permissions endpoint
     * and persist them in localStorage. Dispatches a `permissions_refreshed` event.
     */
    private async fetchAndCachePermissions(): Promise<UserPermissions | null> {
        const accessToken = authStorageService.getAccessToken();
        if (!accessToken) {
            return null;
        }
        try {
            const data: MyPermissionsResponse = await apiService.fetch(`${config.apiBaseUrl}/my_permissions`, {
                method: HTTPMethod.GET,
                headers: { Authorization: accessToken },
                noCache: true,
            });

            // tnc_consent and is_internal_user are identity fields that remain in the JWT
            let tncConsent = false;
            let isInternalUser = false;
            try {
                const decoded = jwtDecode<CustomJwtPayload>(accessToken);
                tncConsent = decoded.tncConsent ?? false;
                isInternalUser = decoded.isInternalUser ?? false;
            } catch {
                // leave defaults if token is unreadable
            }

            const permissions: UserPermissions = {
                customerLevelPermissions: data.customerLevelPermissions ?? [],
                projectLevelPermissions: data.projectLevelPermissions ?? [],
                tnc_consent: tncConsent,
                is_internal_user: isInternalUser,
                token: accessToken,
            };

            authStorageService.setPermissions(permissions);

            window.dispatchEvent(
                new CustomEvent('permissions_refreshed', {
                    detail: permissions,
                    bubbles: true,
                })
            );

            return permissions;
        } catch (error) {
            console.info('Failed to fetch permissions:', (error as Error).message);
            return null;
        }
    }

    /**
     * Exchange an OAuth code for tokens, then immediately fetch and cache permissions.
     * If no code is provided, returns the cached permissions (re-fetching if stale).
     */
    public async fetchUserPermissions(code?: string): Promise<UserPermissions> {
        const accessToken = authStorageService.getAccessToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();
        const cached = authStorageService.getPermissions();

        // Return cached permissions if we have a valid token and a warm cache
        if (cached && accessToken && accessTokenExpiry && new Date(accessTokenExpiry) > new Date()) {
            return cached;
        }

        // First-time login: exchange the OAuth code for tokens then fetch permissions
        if (code) {
            try {
                const authData = await apiService.fetch(`${config.apiBaseUrl}/login`, {
                    method: HTTPMethod.POST,
                    body: JSON.stringify({ code, provider: 'aws' }),
                });
                let permissions = await this.storeAuthData(authData);
                if (!permissions) {
                    permissions = await this.fetchAndCachePermissions();
                }
                if (permissions) {
                    return permissions;
                }

                const authError = new Error('Login succeeded but permissions could not be loaded');
                authError.name = 'AuthenticationError';
                throw authError;
            } catch (error) {
                console.info('Token exchange failed:', (error as Error).message);
                const authError = new Error('Login failed - please try again');
                authError.name = 'AuthenticationError';
                throw authError;
            }
        }

        // No code and no valid cache — sign out
        authStorageService.clearAll();
        this.signout();
        const authError = new Error('Authentication required - please log in again');
        authError.name = 'AuthenticationError';
        throw authError;
    }

    /**
     * Return cached permissions from localStorage.
     */
    public getUserPermissions(): UserPermissions | null {
        return authStorageService.getPermissions();
    }

    /**
     * Manually trigger a permissions refresh from the API.
     * Useful for page focus events, admin-triggered permission changes, etc.
     */
    public async refreshPermissions(): Promise<UserPermissions | null> {
        return this.fetchAndCachePermissions();
    }

    /**
     * Get current user identity data from the access token.
     */
    public getCurrentUserData() {
        const accessToken = authStorageService.getAccessToken();
        const refreshToken = authStorageService.getRefreshToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();
        const refreshTokenExpiry = authStorageService.getRefreshTokenExpiry();

        if (!accessToken) {
            return null;
        }

        try {
            const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);

            return {
                cognitoId: decodedToken.cognitoId,
                accessToken: accessToken,
                refreshToken: refreshToken || '',
                jwtToken: accessToken,
                email: decodedToken.userEmail,
                groups: decodedToken.groups,
                lastName: decodedToken.lastName,
                firstName: decodedToken.firstName,
                accessTokenExpiry: accessTokenExpiry,
                refreshTokenExpiry: refreshTokenExpiry,
            };
        } catch (_error) {
            console.info('Invalid token detected in getCurrentUserData, clearing auth data');
            authStorageService.clearAll();
            return null;
        }
    }

    /**
     * Get access token with automatic refresh and retry logic.
     */
    public async getAccessToken(): Promise<string | null> {
        const accessToken = authStorageService.getAccessToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();

        if (accessToken && accessTokenExpiry && new Date(accessTokenExpiry) > new Date()) {
            return accessToken;
        }

        // Wait for the renewal to conclude, not for a stopwatch. A renewal
        // that is retrying can legitimately outlast any fixed deadline, and
        // signing someone out for being slow is the failure this replaces.
        if (this.refreshInFlight) {
            await this.refreshInFlight;
            return authStorageService.getAccessToken();
        }

        const rt = authStorageService.getRefreshToken();
        const rtExp = authStorageService.getRefreshTokenExpiry();
        if (!rt || !rtExp || new Date(rtExp) < new Date()) {
            return null;
        }

        await this.refreshSession();
        return authStorageService.getAccessToken();
    }
}
const authService = new AuthService();
export default authService;
